import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Volume2, MessageCircle, BookOpen, Check, ChevronRight, Star, Play, Pause } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { selectMaleVoice, selectFemaleVoice, preloadVoices } from '@/services/voiceService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Guided lesson content for each scenario
const GUIDED_LESSONS = {
  restaurant: {
    title: 'No Restaurante',
    emoji: '🍽️',
    description: 'Aprenda a fazer pedidos e interagir em restaurantes',
    image: 'https://images.pexels.com/photos/29489315/pexels-photo-29489315.jpeg?auto=compress&cs=tinysrgb&w=600',
    keyPhrases: [
      { en: "A table for two, please.", pt: "Uma mesa para dois, por favor." },
      { en: "Can I see the menu?", pt: "Posso ver o cardápio?" },
      { en: "What do you recommend?", pt: "O que você recomenda?" },
      { en: "I'll have the...", pt: "Vou querer o/a..." },
      { en: "Can I have the check, please?", pt: "Pode trazer a conta, por favor?" },
      { en: "Is service included?", pt: "A gorjeta está inclusa?" },
    ],
    dialogue: [
      { speaker: 'waiter', text: "Good evening! Welcome to our restaurant. Do you have a reservation?" },
      { speaker: 'you', text: "Good evening! Yes, a table for two under the name Smith." },
      { speaker: 'waiter', text: "Perfect! Right this way, please. Here's the menu." },
      { speaker: 'you', text: "Thank you. What do you recommend today?" },
      { speaker: 'waiter', text: "Our special today is the grilled salmon. It's excellent!" },
      { speaker: 'you', text: "That sounds great! I'll have the salmon, please." },
    ],
    tips: [
      "Sempre diga 'please' ao fazer pedidos",
      "É comum perguntar 'What do you recommend?'",
      "Nos EUA, gorjeta de 15-20% é esperada"
    ]
  },
  airport: {
    title: 'No Aeroporto',
    emoji: '✈️',
    description: 'Navegue pelo aeroporto com confiança',
    image: 'https://images.unsplash.com/photo-1773734874748-8e7893de8e6f?w=600&h=400&fit=crop',
    keyPhrases: [
      { en: "Where is the check-in counter?", pt: "Onde fica o balcão de check-in?" },
      { en: "I'd like a window seat.", pt: "Eu gostaria de um assento na janela." },
      { en: "How much luggage can I bring?", pt: "Quanta bagagem posso levar?" },
      { en: "Where is Gate B12?", pt: "Onde fica o portão B12?" },
      { en: "Is the flight on time?", pt: "O voo está no horário?" },
      { en: "I need to declare something.", pt: "Preciso declarar algo." },
    ],
    dialogue: [
      { speaker: 'agent', text: "Good morning! May I see your passport and ticket, please?" },
      { speaker: 'you', text: "Good morning! Here you go." },
      { speaker: 'agent', text: "Are you checking any bags today?" },
      { speaker: 'you', text: "Yes, just one suitcase. I'd like an aisle seat, please." },
      { speaker: 'agent', text: "Of course. Your flight leaves from Gate C15. Boarding starts at 10:30." },
      { speaker: 'you', text: "Thank you! Where is Gate C15?" },
    ],
    tips: [
      "Chegue 2-3 horas antes de voos internacionais",
      "Tenha documentos sempre à mão",
      "Aisle seat = corredor, Window seat = janela"
    ]
  },
  job_interview: {
    title: 'Entrevista de Emprego',
    emoji: '💼',
    description: 'Prepare-se para entrevistas em inglês',
    image: 'https://images.unsplash.com/photo-1637665662134-db459c1bbb46?w=600&h=400&fit=crop',
    keyPhrases: [
      { en: "Tell me about yourself.", pt: "Fale sobre você." },
      { en: "I have experience in...", pt: "Tenho experiência em..." },
      { en: "My strengths are...", pt: "Meus pontos fortes são..." },
      { en: "I'm looking for a challenging position.", pt: "Estou procurando uma posição desafiadora." },
      { en: "When can I start?", pt: "Quando posso começar?" },
      { en: "What are the next steps?", pt: "Quais são os próximos passos?" },
    ],
    dialogue: [
      { speaker: 'interviewer', text: "Nice to meet you! Please, have a seat. Tell me about yourself." },
      { speaker: 'you', text: "Thank you! I'm a software developer with 5 years of experience." },
      { speaker: 'interviewer', text: "What interests you about this position?" },
      { speaker: 'you', text: "I'm excited about the opportunity to work on innovative projects." },
      { speaker: 'interviewer', text: "What would you say is your greatest strength?" },
      { speaker: 'you', text: "I'm very organized and I work well under pressure." },
    ],
    tips: [
      "Pesquise sobre a empresa antes",
      "Prepare exemplos concretos de realizações",
      "Faça perguntas sobre a posição no final"
    ]
  },
  hotel: {
    title: 'No Hotel',
    emoji: '🏨',
    description: 'Check-in, serviços e problemas comuns',
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&h=400&fit=crop',
    keyPhrases: [
      { en: "I have a reservation.", pt: "Eu tenho uma reserva." },
      { en: "What time is checkout?", pt: "Que horas é o checkout?" },
      { en: "Is breakfast included?", pt: "O café da manhã está incluso?" },
      { en: "Can I have a late checkout?", pt: "Posso fazer checkout mais tarde?" },
      { en: "The AC isn't working.", pt: "O ar-condicionado não está funcionando." },
      { en: "I need extra towels.", pt: "Preciso de toalhas extras." },
    ],
    dialogue: [
      { speaker: 'receptionist', text: "Welcome! Do you have a reservation with us?" },
      { speaker: 'you', text: "Yes, under the name Johnson. For three nights." },
      { speaker: 'receptionist', text: "Perfect! I have you in room 405. Here's your key card." },
      { speaker: 'you', text: "Thank you. Is breakfast included?" },
      { speaker: 'receptionist', text: "Yes, breakfast is served from 7 to 10 AM in the restaurant." },
      { speaker: 'you', text: "Great! What time is checkout?" },
    ],
    tips: [
      "Confirme todos os serviços incluídos",
      "Guarde o cartão do quarto com cuidado",
      "Não hesite em pedir ajuda na recepção"
    ]
  },
  shopping: {
    title: 'Fazendo Compras',
    emoji: '🛍️',
    description: 'Compras, tamanhos e trocas',
    image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=400&fit=crop',
    keyPhrases: [
      { en: "Do you have this in a medium?", pt: "Você tem isso em tamanho médio?" },
      { en: "Can I try this on?", pt: "Posso experimentar?" },
      { en: "Where are the fitting rooms?", pt: "Onde ficam os provadores?" },
      { en: "How much is this?", pt: "Quanto custa isso?" },
      { en: "Do you accept credit cards?", pt: "Vocês aceitam cartão de crédito?" },
      { en: "Can I return this?", pt: "Posso devolver isso?" },
    ],
    dialogue: [
      { speaker: 'assistant', text: "Hi! Can I help you find something?" },
      { speaker: 'you', text: "Yes, I'm looking for a blue shirt." },
      { speaker: 'assistant', text: "Sure! What size do you need?" },
      { speaker: 'you', text: "Medium, please. Can I try it on?" },
      { speaker: 'assistant', text: "Of course! The fitting rooms are right over there." },
      { speaker: 'you', text: "Thank you. How much is this shirt?" },
    ],
    tips: [
      "Tamanhos podem variar entre países",
      "Sempre pergunte sobre a política de troca",
      "É ok pedir desconto em algumas lojas"
    ]
  },
  doctor: {
    title: 'No Médico',
    emoji: '🏥',
    description: 'Descreva sintomas e entenda orientações',
    image: 'https://images.unsplash.com/photo-1551076805-e1869033e561?w=600&h=400&fit=crop',
    keyPhrases: [
      { en: "I have a headache.", pt: "Estou com dor de cabeça." },
      { en: "I've been feeling tired.", pt: "Tenho me sentido cansado." },
      { en: "It hurts when I...", pt: "Dói quando eu..." },
      { en: "How often should I take this?", pt: "Com que frequência devo tomar isso?" },
      { en: "I'm allergic to...", pt: "Sou alérgico a..." },
      { en: "Do I need a prescription?", pt: "Preciso de receita?" },
    ],
    dialogue: [
      { speaker: 'doctor', text: "Hello! What brings you in today?" },
      { speaker: 'you', text: "I've had a bad cough for about a week." },
      { speaker: 'doctor', text: "I see. Do you have any other symptoms? Fever? Headache?" },
      { speaker: 'you', text: "Yes, I've had a slight fever and I feel very tired." },
      { speaker: 'doctor', text: "Let me take a look. Are you allergic to any medications?" },
      { speaker: 'you', text: "No, I don't have any allergies." },
    ],
    tips: [
      "Descreva sintomas de forma clara e direta",
      "Mencione alergias antes de receber receitas",
      "Pergunte sobre efeitos colaterais"
    ]
  },
  gym: {
    title: 'Na Academia',
    emoji: '🏋️',
    description: 'Vocabulário de exercícios e equipamentos',
    image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600&h=400&fit=crop',
    keyPhrases: [
      { en: "Where are the weights?", pt: "Onde ficam os pesos?" },
      { en: "Is this machine available?", pt: "Esta máquina está disponível?" },
      { en: "How many sets do you have left?", pt: "Quantas séries faltam?" },
      { en: "Can you spot me?", pt: "Pode me ajudar com segurança?" },
      { en: "I'd like to sign up for a class.", pt: "Gostaria de me inscrever em uma aula." },
      { en: "What time does the gym close?", pt: "Que horas a academia fecha?" },
    ],
    dialogue: [
      { speaker: 'trainer', text: "Hi! Are you new here? Do you need any help?" },
      { speaker: 'you', text: "Yes, it's my first day. Where can I find the treadmills?" },
      { speaker: 'trainer', text: "They're in the cardio area, just past the weights." },
      { speaker: 'you', text: "Thanks! Do you have any group classes?" },
      { speaker: 'trainer', text: "Yes! We have yoga at 6 PM and spinning at 7 PM." },
      { speaker: 'you', text: "Great! How do I sign up for the yoga class?" },
    ],
    tips: [
      "Limpe os equipamentos após usar",
      "É educado perguntar antes de usar uma máquina",
      "'Spot me' significa ajudar com segurança no levantamento de peso"
    ]
  },
  casual: {
    title: 'Conversa Casual',
    emoji: '💬',
    description: 'Small talk e conversas do dia a dia',
    image: 'https://images.unsplash.com/photo-1543807535-eceef0bc6599?w=600&h=400&fit=crop',
    keyPhrases: [
      { en: "How's it going?", pt: "Como vai?" },
      { en: "What do you do for a living?", pt: "O que você faz da vida?" },
      { en: "That's interesting!", pt: "Que interessante!" },
      { en: "I totally agree.", pt: "Concordo totalmente." },
      { en: "What do you think about...?", pt: "O que você acha de...?" },
      { en: "It was nice talking to you!", pt: "Foi bom conversar com você!" },
    ],
    dialogue: [
      { speaker: 'friend', text: "Hey! How's it going?" },
      { speaker: 'you', text: "Pretty good! Just busy with work. How about you?" },
      { speaker: 'friend', text: "Same here! Did you do anything fun this weekend?" },
      { speaker: 'you', text: "Yes, I went to a new restaurant downtown. It was amazing!" },
      { speaker: 'friend', text: "Oh nice! What kind of food?" },
      { speaker: 'you', text: "Italian. The pasta was incredible. You should try it!" },
    ],
    tips: [
      "Small talk é importante para criar conexões",
      "Mostre interesse fazendo perguntas de seguimento",
      "Expressões como 'That's cool!' mostram engajamento"
    ]
  },
  // NEW LESSONS
  bank: {
    title: 'No Banco',
    emoji: '🏦',
    description: 'Abrir conta, fazer depósitos e resolver problemas',
    image: 'https://images.unsplash.com/photo-1541354329998-f4d9a9f9297f?w=600&h=400&fit=crop',
    keyPhrases: [
      { en: "I'd like to open an account.", pt: "Gostaria de abrir uma conta." },
      { en: "What's the interest rate?", pt: "Qual é a taxa de juros?" },
      { en: "I need to make a transfer.", pt: "Preciso fazer uma transferência." },
      { en: "Can I get a statement?", pt: "Posso obter um extrato?" },
      { en: "My card was stolen.", pt: "Meu cartão foi roubado." },
      { en: "What are the fees?", pt: "Quais são as taxas?" },
    ],
    dialogue: [
      { speaker: 'teller', text: "Good morning! How can I help you today?" },
      { speaker: 'you', text: "Hi! I'd like to open a savings account." },
      { speaker: 'teller', text: "Of course! Do you have an ID and proof of address?" },
      { speaker: 'you', text: "Yes, here's my passport and a utility bill." },
      { speaker: 'teller', text: "Perfect. Would you like a debit card as well?" },
      { speaker: 'you', text: "Yes, please. What's the monthly fee?" },
    ],
    tips: [
      "Leve sempre dois documentos de identificação",
      "Pergunte sobre taxas ocultas antes de assinar",
      "ATM = caixa eletrônico"
    ]
  },
  pharmacy: {
    title: 'Na Farmácia',
    emoji: '💊',
    description: 'Comprar medicamentos e pedir orientações',
    image: 'https://images.unsplash.com/photo-1585435557343-3b092031a831?w=600&h=400&fit=crop',
    keyPhrases: [
      { en: "Do you have something for headaches?", pt: "Você tem algo para dor de cabeça?" },
      { en: "I need to fill this prescription.", pt: "Preciso aviar esta receita." },
      { en: "How often should I take this?", pt: "Com que frequência devo tomar isso?" },
      { en: "Are there any side effects?", pt: "Há algum efeito colateral?" },
      { en: "Do I need a prescription for this?", pt: "Preciso de receita para isso?" },
      { en: "Is this available over the counter?", pt: "Isso é vendido sem receita?" },
    ],
    dialogue: [
      { speaker: 'pharmacist', text: "Hello! How can I help you?" },
      { speaker: 'you', text: "Hi, I have a bad cold. Do you have anything for congestion?" },
      { speaker: 'pharmacist', text: "Yes, this nasal spray works well. Take it twice a day." },
      { speaker: 'you', text: "Thank you. Are there any side effects?" },
      { speaker: 'pharmacist', text: "You might feel a bit drowsy. Don't drive after taking it." },
      { speaker: 'you', text: "Got it. I'll also take some vitamin C, please." },
    ],
    tips: [
      "OTC = Over the counter (sem receita)",
      "Sempre mencione alergias a medicamentos",
      "Guarde recibos para reembolso de seguro"
    ]
  },
  supermarket: {
    title: 'No Supermercado',
    emoji: '🛒',
    description: 'Fazer compras e encontrar produtos',
    image: 'https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=600&h=400&fit=crop',
    keyPhrases: [
      { en: "Where can I find the dairy section?", pt: "Onde fica a seção de laticínios?" },
      { en: "Is this on sale?", pt: "Isso está em promoção?" },
      { en: "Do you have this in a larger size?", pt: "Vocês têm isso em tamanho maior?" },
      { en: "Paper or plastic?", pt: "Sacola de papel ou plástico?" },
      { en: "Can I pay with card?", pt: "Posso pagar com cartão?" },
      { en: "I forgot my loyalty card.", pt: "Esqueci meu cartão fidelidade." },
    ],
    dialogue: [
      { speaker: 'employee', text: "Hi there! Can I help you find something?" },
      { speaker: 'you', text: "Yes, where can I find gluten-free bread?" },
      { speaker: 'employee', text: "It's in aisle 3, next to the regular bread." },
      { speaker: 'you', text: "Great, thanks! Is there a discount on organic products today?" },
      { speaker: 'employee', text: "Yes! All organic items are 20% off this week." },
      { speaker: 'you', text: "Perfect! I'll grab a few things. Where's the checkout?" },
    ],
    tips: [
      "Aisle = corredor do supermercado",
      "Self-checkout = caixa de autoatendimento",
      "Bring your own bags = traga suas próprias sacolas"
    ]
  },
  directions: {
    title: 'Pedindo Direções',
    emoji: '🗺️',
    description: 'Perguntar e entender direções na rua',
    image: 'https://images.unsplash.com/photo-1524661135-423995f22d0b?w=600&h=400&fit=crop',
    keyPhrases: [
      { en: "Excuse me, how do I get to...?", pt: "Com licença, como chego em...?" },
      { en: "Is it far from here?", pt: "É longe daqui?" },
      { en: "Turn left at the corner.", pt: "Vire à esquerda na esquina." },
      { en: "Go straight for two blocks.", pt: "Siga em frente por duas quadras." },
      { en: "It's across from the bank.", pt: "É em frente ao banco." },
      { en: "Can you show me on the map?", pt: "Pode me mostrar no mapa?" },
    ],
    dialogue: [
      { speaker: 'stranger', text: "Excuse me, do you know where the train station is?" },
      { speaker: 'you', text: "Sure! Go straight for about 5 minutes." },
      { speaker: 'stranger', text: "And then?" },
      { speaker: 'you', text: "Turn right at the traffic light. You'll see it on your left." },
      { speaker: 'stranger', text: "Is it walking distance?" },
      { speaker: 'you', text: "Yes, about 10 minutes on foot. You can't miss it!" },
    ],
    tips: [
      "Block = quadra, Intersection = cruzamento",
      "On foot = a pé, By car = de carro",
      "Landmark = ponto de referência (use para dar direções)"
    ]
  },
  emergency: {
    title: 'Emergências',
    emoji: '🚨',
    description: 'Pedir ajuda em situações urgentes',
    image: 'https://images.unsplash.com/photo-1587745416684-47953f16f02f?w=600&h=400&fit=crop',
    keyPhrases: [
      { en: "Help! I need assistance!", pt: "Socorro! Preciso de ajuda!" },
      { en: "Call an ambulance!", pt: "Chame uma ambulância!" },
      { en: "There's been an accident.", pt: "Houve um acidente." },
      { en: "I lost my passport.", pt: "Perdi meu passaporte." },
      { en: "Someone stole my wallet.", pt: "Alguém roubou minha carteira." },
      { en: "Where is the nearest hospital?", pt: "Onde fica o hospital mais próximo?" },
    ],
    dialogue: [
      { speaker: 'operator', text: "911, what's your emergency?" },
      { speaker: 'you', text: "There's been a car accident on Main Street!" },
      { speaker: 'operator', text: "Is anyone injured?" },
      { speaker: 'you', text: "Yes, one person is hurt. Please send an ambulance!" },
      { speaker: 'operator', text: "Help is on the way. Can you tell me the exact location?" },
      { speaker: 'you', text: "It's at the intersection of Main and Oak Street." },
    ],
    tips: [
      "911 é o número de emergência nos EUA",
      "Mantenha a calma e fale devagar",
      "Sempre saiba o endereço onde você está"
    ]
  },
  coffee_shop: {
    title: 'Na Cafeteria',
    emoji: '☕',
    description: 'Pedir café e interagir em cafeterias',
    image: 'https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=600&h=400&fit=crop',
    keyPhrases: [
      { en: "I'll have a large latte, please.", pt: "Vou querer um latte grande, por favor." },
      { en: "Can I get that with oat milk?", pt: "Pode ser com leite de aveia?" },
      { en: "For here or to go?", pt: "Para comer aqui ou para viagem?" },
      { en: "What's your WiFi password?", pt: "Qual é a senha do WiFi?" },
      { en: "Do you have any pastries?", pt: "Vocês têm doces/salgados?" },
      { en: "Can I get an extra shot of espresso?", pt: "Pode colocar uma dose extra de espresso?" },
    ],
    dialogue: [
      { speaker: 'barista', text: "Hi! What can I get for you?" },
      { speaker: 'you', text: "I'll have a medium cappuccino, please." },
      { speaker: 'barista', text: "Hot or iced?" },
      { speaker: 'you', text: "Hot, please. And can I get it with almond milk?" },
      { speaker: 'barista', text: "Of course! Anything else? We have fresh croissants." },
      { speaker: 'you', text: "Sure, I'll take one. What's the WiFi password?" },
    ],
    tips: [
      "Tamanhos: Tall (pequeno), Grande (médio), Venti (grande)",
      "For here = para cá, To go = para levar",
      "É comum deixar gorjeta no pote ao lado do caixa"
    ]
  }
};

const GuidedLessonPage = () => {
  const navigate = useNavigate();
  const { scenarioId } = useParams();
  const { user } = useAuth();
  
  const [currentTab, setCurrentTab] = useState('phrases'); // phrases, dialogue, tips
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [completedPhrases, setCompletedPhrases] = useState([]);
  const [dialogueStep, setDialogueStep] = useState(0);
  
  const lesson = GUIDED_LESSONS[scenarioId] || GUIDED_LESSONS.restaurant;
  const synthRef = React.useRef(window.speechSynthesis);

  React.useEffect(() => {
    preloadVoices();
  }, []);

  const speakText = (text, gender = 'female') => {
    if (isSpeaking) {
      synthRef.current.cancel();
      setIsSpeaking(false);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    const targetLang = user?.target_language || 'English';
    
    const voice = gender === 'male' ? selectMaleVoice(targetLang) : selectFemaleVoice(targetLang);
    if (voice) {
      utterance.voice = voice;
      utterance.lang = voice.lang;
    }
    
    utterance.rate = 0.85;
    utterance.pitch = gender === 'male' ? 0.88 : 1.02;
    
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    
    synthRef.current.speak(utterance);
  };

  const markPhraseComplete = (index) => {
    if (!completedPhrases.includes(index)) {
      setCompletedPhrases([...completedPhrases, index]);
      toast.success('+5 XP! 🎉');
      
      // Add XP
      const token = localStorage.getItem('token');
      axios.post(`${API}/gamification/add-xp`, { xp: 5, action: 'guided_phrase' }, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    }
    
    if (currentPhraseIndex < lesson.keyPhrases.length - 1) {
      setCurrentPhraseIndex(currentPhraseIndex + 1);
    }
  };

  const startPractice = () => {
    navigate(`/scenarios`);
    toast.success('Agora pratique em uma conversa real! 💬');
  };

  const progress = (completedPhrases.length / lesson.keyPhrases.length) * 100;

  return (
    <div className="min-h-screen bg-sand" data-testid="guided-lesson-page">
      {/* Header with Image */}
      <div className="relative h-48 overflow-hidden">
        <img 
          src={lesson.image} 
          alt={lesson.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        
        <Button
          onClick={() => navigate('/scenarios')}
          variant="ghost"
          className="absolute top-4 left-4 text-white hover:bg-white/20"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        
        <div className="absolute bottom-4 left-4 right-4">
          <span className="text-4xl mb-2 block">{lesson.emoji}</span>
          <h1 className="font-fraunces text-2xl font-semibold text-white">{lesson.title}</h1>
          <p className="text-white/80 text-sm">{lesson.description}</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="px-4 py-3 bg-white border-b">
        <div className="flex items-center gap-3">
          <div className="flex-1 h-2 bg-forest/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-lime transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-xs text-forest font-medium">
            {completedPhrases.length}/{lesson.keyPhrases.length}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 py-2 bg-white flex gap-2 border-b">
        {[
          { id: 'phrases', label: 'Frases', icon: BookOpen },
          { id: 'dialogue', label: 'Diálogo', icon: MessageCircle },
          { id: 'tips', label: 'Dicas', icon: Star },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setCurrentTab(tab.id)}
            className={`flex-1 py-3 px-4 rounded-xl flex items-center justify-center gap-2 text-sm font-medium transition-all ${
              currentTab === tab.id
                ? 'bg-forest text-sand'
                : 'bg-sand text-forest hover:bg-forest/10'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="px-4 py-6 pb-32">
        <AnimatePresence mode="wait">
          
          {/* Phrases Tab */}
          {currentTab === 'phrases' && (
            <motion.div
              key="phrases"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              <h2 className="font-semibold text-forest mb-4">Frases Essenciais</h2>
              
              {lesson.keyPhrases.map((phrase, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-white rounded-2xl p-4 shadow-soft ${
                    completedPhrases.includes(index) ? 'border-2 border-lime' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="font-medium text-forest text-lg mb-1">"{phrase.en}"</p>
                      <p className="text-stone text-sm">{phrase.pt}</p>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => speakText(phrase.en)}
                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                          isSpeaking ? 'bg-purple-500 text-white' : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                        }`}
                      >
                        {isSpeaking ? <Pause className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                      </button>
                      
                      {!completedPhrases.includes(index) && (
                        <button
                          onClick={() => markPhraseComplete(index)}
                          className="w-10 h-10 rounded-full bg-lime text-forest flex items-center justify-center hover:bg-lime/80"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      
                      {completedPhrases.includes(index) && (
                        <div className="w-10 h-10 rounded-full bg-lime/30 text-forest flex items-center justify-center">
                          <Check className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Dialogue Tab */}
          {currentTab === 'dialogue' && (
            <motion.div
              key="dialogue"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <h2 className="font-semibold text-forest mb-4">Diálogo de Exemplo</h2>
              
              <div className="space-y-3">
                {lesson.dialogue.slice(0, dialogueStep + 1).map((line, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${line.speaker === 'you' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div 
                      className={`max-w-[80%] rounded-2xl p-4 ${
                        line.speaker === 'you' 
                          ? 'bg-forest text-sand rounded-br-none' 
                          : 'bg-white shadow-soft rounded-bl-none'
                      }`}
                    >
                      <p className="text-xs opacity-70 mb-1 capitalize">
                        {line.speaker === 'you' ? 'Você' : line.speaker}
                      </p>
                      <p className={`text-sm ${line.speaker === 'you' ? 'text-sand' : 'text-forest'}`}>
                        {line.text}
                      </p>
                      
                      <button
                        onClick={() => speakText(line.text, line.speaker === 'you' ? 'male' : 'female')}
                        className={`mt-2 text-xs flex items-center gap-1 ${
                          line.speaker === 'you' ? 'text-sand/70 hover:text-sand' : 'text-forest/60 hover:text-forest'
                        }`}
                      >
                        <Volume2 className="w-3 h-3" />
                        Ouvir
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              {dialogueStep < lesson.dialogue.length - 1 && (
                <Button
                  onClick={() => setDialogueStep(dialogueStep + 1)}
                  className="w-full mt-6 bg-forest text-sand rounded-full"
                >
                  Próxima fala
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              
              {dialogueStep >= lesson.dialogue.length - 1 && (
                <div className="mt-6 text-center">
                  <p className="text-forest font-medium mb-3">🎉 Diálogo completo!</p>
                  <Button
                    onClick={() => setDialogueStep(0)}
                    variant="outline"
                    className="rounded-full"
                  >
                    Repetir diálogo
                  </Button>
                </div>
              )}
            </motion.div>
          )}

          {/* Tips Tab */}
          {currentTab === 'tips' && (
            <motion.div
              key="tips"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <h2 className="font-semibold text-forest mb-4">Dicas Culturais</h2>
              
              <div className="space-y-3">
                {lesson.tips.map((tip, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-gradient-to-r from-lime/20 to-emerald-100/50 rounded-2xl p-4 flex items-start gap-3"
                  >
                    <span className="text-lg">💡</span>
                    <p className="text-forest text-sm flex-1">{tip}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>

      {/* Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t">
        <Button
          onClick={startPractice}
          className="w-full bg-gradient-to-r from-forest to-emerald-600 text-sand rounded-full py-6 text-lg font-medium"
        >
          <MessageCircle className="w-5 h-5 mr-2" />
          Praticar com a IA
        </Button>
      </div>
    </div>
  );
};

export default GuidedLessonPage;
